/**
 * No modo "Próximos dias", o que sobra do observado sai do mapa e a foto de
 * satélite do fundo (map/fundoCreditos.ts, com as ruas e os nomes) vira o
 * fundo, em todos os zooms.
 *
 * As camadas observadas com interruptor (satélite, radar, focos, fumaça,
 * qualidade do ar, estações, rios, sondagem, barcos, mapa) são DESLIGADAS ao
 * entrar (`limpar.ts`); se a pessoa religar uma durante o modo, ela aparece.
 * Aqui ficam só as que não têm interruptor próprio: o rastreio e a previsão
 * dele (com a trajetória), o rastreio da chuva, o contorno da célula do CAMS e a máscara do filtro
 * dos "Últimos focos".
 *
 * Como, sem tocar nas stores delas e sem brigar com quem decide o `visible` de
 * cada uma: a camada ganha `maxResolution = 0` — o OpenLayers não a desenha
 * nem a considera no toque em nenhum zoom — e volta à resolução que tinha ao
 * sair. A foto do fundo e os rótulos dela, que fora do modo só aparecem depois
 * do zoom máximo do GOES, ganham `maxResolution = Infinity` do mesmo jeito. Nenhum desses módulos
 * muda `maxResolution` depois de criar a camada (conferido em 18/10/2026).
 * Camadas criadas durante o modo são pegas no `add`.
 */
import type BaseLayer from 'ol/layer/Base';
import type OlMap from 'ol/Map';
import { Z } from '../map/zIndex';
import { aoMudarModo, emModoPrevisao } from './store';

export const Z_OCULTAS: ReadonlySet<number> = new Set([
  Z.TRACKING_FORECAST,
  Z.TRACKING_CHUVA,
  Z.TRACKING,
  Z.TRACKING_TRAJETORIA,
  Z.AR_MODELO_DESTAQUE,
  Z.AREA_FOCUS,
]);

export const RESUMO_OCULTAS = 'camadas observadas desligadas durante a previsão';

const CHAVE = 'previsao:maxResolution';

/** As camadas do fundo (foto e rótulos), que no modo cobrem todos os zooms. */
const Z_FUNDO: ReadonlySet<number> = new Set([Z.FUNDO, Z.FUNDO_ROTULOS]);

function ocultar(l: BaseLayer): void {
  const z = l.getZIndex() ?? NaN;
  if ((!Z_OCULTAS.has(z) && !Z_FUNDO.has(z)) || l.get(CHAVE) !== undefined) return;
  l.set(CHAVE, l.getMaxResolution());
  l.setMaxResolution(Z_FUNDO.has(z) ? Infinity : 0);
}

function mostrar(l: BaseLayer): void {
  const antes = l.get(CHAVE) as number | undefined;
  if (antes === undefined) return;
  l.setMaxResolution(antes);
  l.unset(CHAVE);
}

/** Liga o ocultador (e a foto ao fundo) ao mapa; devolve a função que desfaz tudo. */
export function iniciarOcultador(map: OlMap): () => void {
  const camadas = map.getLayers();
  const aplicar = (modo: boolean) => camadas.forEach((l) => (modo ? ocultar(l) : mostrar(l)));
  const aoAdicionar = (e: { element: BaseLayer }) => {
    if (emModoPrevisao()) ocultar(e.element);
  };
  camadas.on('add', aoAdicionar);
  const parar = aoMudarModo(aplicar);
  if (emModoPrevisao()) aplicar(true);
  return () => {
    parar();
    camadas.un('add', aoAdicionar);
    aplicar(false);
  };
}
